import { useEffect, useRef } from 'react'
import { LineLayer, PointLayer, PolygonLayer, RasterLayer } from '@antv/l7'
import type { ILayer, Scene } from '@antv/l7'
import { useL7Map } from '../../../../hooks/useL7Map'
import { circlePolygon, estimateIntensityRadiusKm } from '../../../../lib/geo'
import { CITIES } from '../../../../lib/seismic'

interface PickMapProps {
  lat: number
  lon: number
  magnitude: number
  onPick: (lat: number, lon: number) => void
}

type PickLayers = {
  cityDots: ILayer
  intensityFill: ILayer
  intensityLine: ILayer
  marker: ILayer
}

const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] }

/**
 * 调试台选点地图：暗色底图 + 点击取震中坐标。
 * 震中三角标 + 随震级变化的烈度估计圈（与指挥屏同一套估算逻辑）。
 * 城市参考点固定展示，选中位置由表单驱动。
 */
export function PickMap({ lat, lon, magnitude, onPick }: PickMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scene: sceneRef, ready } = useL7Map(containerRef, { center: [87.5, 43.6], zoom: 6 })
  const onPickRef = useRef(onPick)
  useEffect(() => {
    onPickRef.current = onPick
  }, [onPick])
  const layersRef = useRef<PickLayers | null>(null)

  // 图层初始化（ready 后一次）
  useEffect(() => {
    const scene: Scene | null = sceneRef.current
    if (!scene || !ready || layersRef.current) return

    const basemap = new RasterLayer({ zIndex: 0 }).source(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { parser: { type: 'rasterTile', tileSize: 256, zoomOffset: 0 } },
    )
    const cityDots = new PointLayer({ zIndex: 4 })
      .source(CITIES, { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('circle')
      .size(4)
      .color('#8b98ab')
      .style({ stroke: '#070b12', strokeWidth: 1 })
    const intensityFill = new PolygonLayer({ zIndex: 2 })
      .source(EMPTY_FC)
      .color('#ff4d5e')
      .style({ opacity: 0.08 })
    const intensityLine = new LineLayer({ zIndex: 2 })
      .source(EMPTY_FC)
      .shape('line')
      .size(1)
      .color('#ff4d5e')
      .style({ opacity: 0.5 })
    const marker = new PointLayer({ zIndex: 5 })
      .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('triangle')
      .size(14)
      .color('#ff4d5e')
      .style({ stroke: '#070b12', strokeWidth: 1.5 })

    layersRef.current = { cityDots, intensityFill, intensityLine, marker }
    scene.addLayer(basemap)
    scene.addLayer(intensityFill)
    scene.addLayer(intensityLine)
    scene.addLayer(cityDots)
    scene.addLayer(marker)
  }, [sceneRef, ready])

  // 选中点与烈度圈随表单更新
  useEffect(() => {
    const layers = layersRef.current
    if (!layers) return
    layers.marker.setData([{ lat, lon }])
    const circle = circlePolygon(lon, lat, estimateIntensityRadiusKm(magnitude))
    const fc = { type: 'FeatureCollection' as const, features: [circle] }
    layers.intensityFill.setData(fc)
    layers.intensityLine.setData(fc)
  }, [lat, lon, magnitude])

  // 点击取点
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene || !ready) return
    const onClick = (ev: { lngLat?: { lng?: number; lat?: number } }) => {
      const lng = ev?.lngLat?.lng
      const pickLat = ev?.lngLat?.lat
      if (lng == null || pickLat == null) return
      onPickRef.current(Number(pickLat.toFixed(4)), Number(lng.toFixed(4)))
    }
    scene.on('click', onClick)
    return () => {
      scene.off('click', onClick)
    }
  }, [sceneRef, ready])

  return (
    <div
      ref={containerRef}
      className="h-[300px] overflow-hidden rounded-md border border-border"
      aria-label="震中选点地图：点击地图选取震中位置"
      role="application"
    />
  )
}
